import type { CSSProperties } from 'react';

/**
 * Hand-painted UI icons (gaps B4, art-src/ui/icons_*, `pnpm assets:ui icons`): 96² WebP, crisp up to 32 CSS px
 * at 3× DPR. Always decorative: the button or text next to it carries the label.
 */
const FILES = import.meta.glob<string>('../art/icons/*.webp', { eager: true, import: 'default' });

const URLS: Record<string, string> = Object.fromEntries(
  Object.entries(FILES).map(([path, url]) => [path.slice(path.lastIndexOf('/') + 1, -'.webp'.length), url]),
);

export const ICON_NAMES = [
  'achievements',
  'album',
  'back',
  'bag',
  'boss',
  'camera',
  'check',
  'close',
  'coin',
  'cooking',
  'credits',
  'dusting',
  'filter',
  'frames',
  'heart',
  'home',
  'ideas',
  'link',
  'lock',
  'mail',
  'menu',
  'minigame',
  'minus',
  'more',
  'music',
  'new',
  'orders',
  'packing',
  'plus',
  'profile',
  'recenter',
  'recipe',
  'rotate',
  'save_photo',
  'search',
  'settings',
  'shop_closed',
  'shop_open',
  'shop_room',
  'shopping',
  'shortcuts',
  'sound_off',
  'sound_on',
  'sparkle',
  'star',
  'stickers',
  'store',
  'town',
  'undo',
  'unlocks',
] as const;

export type IconName = (typeof ICON_NAMES)[number];

export function iconUrl(name: IconName): string | undefined {
  return URLS[name];
}

export function Icon({
  name,
  size = 24,
  flip = false,
  className,
  style,
}: {
  name: IconName;
  size?: number;
  /** Mirrored (redo from undo). */
  flip?: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <img
      src={URLS[name]}
      width={size}
      height={size}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={className}
      style={{
        flex: 'none',
        objectFit: 'contain',
        pointerEvents: 'none',
        userSelect: 'none',
        ...(flip ? { transform: 'scaleX(-1)' } : null),
        ...style,
      }}
    />
  );
}
